import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { type Column, ListTable, type ListState } from '@/components/admin/ListTable';
import {
  fetchContacts,
  fetchThreads,
  THREAD_KIND_LABELS,
  threadHeading,
  type ContactSummary,
  type ThreadKind,
  type ThreadSummary,
} from '@/features/messages/api';
import {
  EMPTY_PEOPLE_FILTERS,
  MessagePeopleFilters,
  peopleFiltersToQuery,
  roleLabel,
  type MessagePeopleFilterValues,
} from '@/features/messages/MessagePeopleFilters';
import { PhotoPreview } from '@/features/members/MemberPhotoField';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

type TabValue = ThreadKind | 'contacts' | null;

function tabs(isOffice: boolean, isStaff: boolean): { value: TabValue; label: string }[] {
  if (isOffice) {
    return [
      { value: 'contacts', label: '連絡先' },
      { value: 'event_group', label: 'グループメッセージ' },
    ];
  }
  if (isStaff) {
    // 事務局チャットと、担当イベントのグループの両方（D-299。D-295の「directのみ」を差し替え）
    return [
      { value: null, label: 'すべて' },
      { value: 'direct', label: '事務局とのチャット' },
      { value: 'event_group', label: 'グループメッセージ' },
    ];
  }
  return [
    { value: null, label: 'すべて' },
    { value: 'direct', label: '事務局への問い合わせ' },
    { value: 'event_group', label: 'グループメッセージ' },
  ];
}

function tabCopy(
  tab: TabValue,
  isOffice: boolean,
  isStaff: boolean,
): { hint: string; emptyTitle: string; emptyDescription: string } {
  if (tab === 'contacts') {
    return {
      hint: '会員・理事講師・サクセス担当の一覧です。クリックするとその人とのやり取りを開けます。まだやり取りが無い相手にも、ここから新しく話しかけられます。',
      emptyTitle: '連絡先がありません',
      emptyDescription: '会員・理事講師・サクセス担当が登録されると、ここに表示されます。',
    };
  }
  if (tab === 'direct') {
    return {
      hint: isStaff
        ? '事務局との1対1のやりとりです。画像やファイルも送れます。右上の「事務局に連絡」から始められます。'
        : '自分が事務局に送った問い合わせです。宛先は事務局に固定されていて、個人を相手に選ぶことはできません。右上の「事務局へ問い合わせ」から新しく始められます。',
      emptyTitle: isStaff
        ? '事務局とのチャットはまだありません'
        : '事務局への問い合わせはまだありません',
      emptyDescription: isStaff
        ? '右上の「事務局に連絡」から始められます。'
        : '右上の「事務局へ問い合わせ」から送れます。事務局の担当者が返信します。',
    };
  }
  if (tab === 'event_group') {
    return {
      hint: isStaff
        ? '担当したイベントの連絡用グループです。公開時に自動で作られ、その回の参加者とやり取りできます。'
        : '月例会・分科会・サクセス面談・1on1など、イベントが公開されたときに自動で作られる連絡用のグループです。その回の参加者と運営が使います。自分では作れません。',
      emptyTitle: 'グループメッセージはまだありません',
      emptyDescription: isOffice
        ? 'イベントを公開すると、ここにスレッドが現れます。'
        : isStaff
          ? '担当したイベントが公開されると、ここにスレッドが現れます。'
          : '参加するイベントが公開されると、ここにスレッドが現れます。',
    };
  }
  return {
    hint: isStaff
      ? '事務局とのチャットと、担当イベントのグループメッセージをまとめて表示しています。'
      : '参加しているスレッドをすべて表示しています。タブを切り替えると、それぞれの種別が何か・いつ作られるかを確認できます。',
    emptyTitle: 'メッセージはありません',
    emptyDescription: isStaff
      ? '事務局への連絡は右上から。担当イベントのグループは、イベント公開時に自動で追加されます。'
      : '事務局への問い合わせは右上から始められます。イベントの連絡は、公開・担当決定のときに自動で追加されます。',
  };
}

/** 事務局の連絡先一覧（FBメッセンジャーのように、相手を選んでやり取りする / D-138）. */
function ContactsList({
  query,
  filterProps,
  tabProps,
}: {
  query: ReturnType<typeof useQuery<ContactSummary[]>>;
  filterProps: {
    draft: MessagePeopleFilterValues;
    onDraftChange: (next: MessagePeopleFilterValues) => void;
    onSearch: () => void;
  };
  tabProps: {
    tabs: { value: TabValue; label: string }[];
    activeTab: TabValue;
    onTabChange: (value: string | null) => void;
  };
}): ReactElement {
  const navigate = useNavigate();
  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  const columns: Column<ContactSummary>[] = [
    {
      key: 'name',
      header: '相手',
      primary: true,
      // 未読はメールソフトと同じく「太字」＋赤丸バッジで示す（FBメッセンジャー方式 / D-138）
      render: (row) => (
        <button
          type="button"
          onClick={() =>
            navigate(
              row.thread_id
                ? `/messages/${row.thread_id}`
                : `/messages/new?recipient=${row.user_id}&name=${encodeURIComponent(row.name)}`,
            )
          }
          className={`flex items-center gap-2 text-left hover:underline ${
            row.unread ? 'font-bold text-wptext-strong' : 'text-wpaccent'
          }`}
        >
          <PhotoPreview photoUrl={row.photo_url} name={row.name} size={28} />
          {row.unread && (
            <span
              aria-label="未読あり"
              className="inline-block h-2 w-2 shrink-0 rounded-full bg-red-600"
            />
          )}
          {row.name}
        </button>
      ),
    },
    {
      key: 'role',
      header: 'ロール',
      render: (row) => roleLabel(row.role),
    },
    {
      key: 'last_message',
      header: '最終メッセージ',
      render: (row) => row.last_message_preview || '（まだやり取りがありません）',
    },
    {
      key: 'last_message_at',
      header: '日時',
      render: (row) =>
        row.last_message_at ? formatDateTime(row.last_message_at) : '—',
    },
  ];

  const copy = tabCopy('contacts', true, false);

  return (
    <>
      <MessagePeopleFilters
        values={filterProps.draft}
        onChange={filterProps.onDraftChange}
        onSearch={filterProps.onSearch}
      />
      <ListTable
        caption="連絡先一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(row) => row.user_id}
        state={state}
        errorMessage="連絡先一覧の取得に失敗しました。"
        onRetry={() => void query.refetch()}
        tabs={tabProps.tabs}
        activeTab={tabProps.activeTab}
        onTabChange={tabProps.onTabChange}
        hint={copy.hint}
        emptyTitle={copy.emptyTitle}
        emptyDescription={copy.emptyDescription}
      />
    </>
  );
}

/** メッセージ一覧（自分が参加しているスレッド。全ロール共通 / 指示書 §12 M8 / D-138）. */
export function ThreadListPage(): ReactElement {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office';
  const isStaff = user?.active_role === 'director' || user?.active_role === 'success';
  const [tab, setTab] = useState<TabValue>(isOffice ? 'contacts' : null);
  const [contactDraftFilters, setContactDraftFilters] =
    useState<MessagePeopleFilterValues>(EMPTY_PEOPLE_FILTERS);
  const [contactAppliedFilters, setContactAppliedFilters] =
    useState<MessagePeopleFilterValues>(EMPTY_PEOPLE_FILTERS);
  const copy = tabCopy(tab, isOffice, isStaff);
  const pageTabs = tabs(isOffice, isStaff);

  const threadsQuery = useQuery({
    queryKey: ['threads', tab],
    queryFn: () => fetchThreads(tab === 'contacts' ? undefined : tab),
    enabled: tab !== 'contacts',
  });
  const contactsQuery = useQuery({
    queryKey: ['threads', 'contacts', contactAppliedFilters],
    queryFn: () => fetchContacts(peopleFiltersToQuery(contactAppliedFilters)),
    enabled: tab === 'contacts',
  });

  let state: ListState = 'ready';
  if (threadsQuery.isPending) state = 'loading';
  else if (threadsQuery.isError) {
    state =
      threadsQuery.error instanceof ApiError && threadsQuery.error.status === 403
        ? 'forbidden'
        : 'error';
  }

  const columns: Column<ThreadSummary>[] = [
    {
      key: 'counterpart',
      // 件名は持たない（D-151）。会話の見出しは「誰と（イベントグループはどの回で）」
      header: '相手・イベント',
      primary: true,
      // 未読はメールソフトと同じく「太字」で示す（D-100）。
      render: (row) => (
        <button
          type="button"
          onClick={() => navigate(`/messages/${row.id}`)}
          className={`text-left hover:underline ${
            row.unread ? 'font-bold text-wptext-strong' : 'text-wpaccent'
          }`}
        >
          {row.unread && <span className="sr-only">未読: </span>}
          {row.kind === 'direct' && isStaff ? '事務局' : threadHeading(row)}
        </button>
      ),
    },
    {
      key: 'kind',
      header: '種別',
      render: (row) =>
        row.kind === 'direct' && isStaff
          ? '事務局とのチャット'
          : THREAD_KIND_LABELS[row.kind],
    },
    {
      key: 'last_message',
      header: '最終メッセージ',
      render: (row) => row.last_message_preview || '（メッセージなし）',
    },
    {
      key: 'updated_at',
      header: '更新日時',
      render: (row) => formatDateTime(row.updated_at),
    },
    {
      key: 'status',
      header: '状態',
      render: (row) => (row.is_closed ? 'クローズ済み' : ''),
    },
  ];

  return (
    <>
      <PageHeader
        title="メッセージ"
        description={
          isStaff
            ? '事務局とのチャットと、担当イベントのグループメッセージが並びます。画像やファイルも添付できます。'
            : '人とのやり取り（返信できるもの）がすべて並びます。システムが自動で送るお知らせは「システム通知」の側です。「グループメッセージ」はイベントが公開されたときに自動で作られます。'
        }
        actions={
          !isOffice ? (
            <button
              type="button"
              onClick={() => navigate('/messages/new')}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
            >
              {isStaff ? '事務局に連絡' : '事務局へ問い合わせ'}
            </button>
          ) : undefined
        }
      />
      {tab === 'contacts' ? (
        <ContactsList
          query={contactsQuery}
          filterProps={{
            draft: contactDraftFilters,
            onDraftChange: setContactDraftFilters,
            onSearch: () => setContactAppliedFilters({ ...contactDraftFilters }),
          }}
          tabProps={{
            tabs: pageTabs,
            activeTab: tab,
            onTabChange: (value) => setTab(value as TabValue),
          }}
        />
      ) : (
        <ListTable
          caption="メッセージ一覧"
          rows={threadsQuery.data ?? []}
          columns={columns}
          rowKey={(row) => row.id}
          state={state}
          errorMessage="メッセージ一覧の取得に失敗しました。"
          onRetry={() => void threadsQuery.refetch()}
          tabs={pageTabs}
          activeTab={tab}
          onTabChange={(value) => setTab(value as TabValue)}
          hint={copy.hint}
          emptyTitle={copy.emptyTitle}
          emptyDescription={copy.emptyDescription}
        />
      )}
    </>
  );
}
